/**
 * Public API call utility — makes API requests WITHOUT auth tokens.
 * Used for public-facing pages like consent forms that don't require login.
 *
 * @param {string} url - API endpoint path (e.g. '/api/v1/public/consent/{token}/')
 * @param {string} method - HTTP method (get, post, put, etc.)
 * @param {object|FormData|null} data - Request body (JSON object or FormData for multipart)
 * @returns {Promise<{ok: boolean, status: number, data?: any, error?: string}>}
 */
const publicApiCall = async (url, method = 'get', data = null) => {
    const baseUrl = import.meta.env.VITE_REACT_APP_HOST_API_KEY;
    const isFormData = data instanceof FormData;
    const headers = {};

    // Only set Content-Type for JSON — browser sets multipart boundary automatically for FormData
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }

    const options = {
        method,
        headers,
    };

    if (data) {
        options.body = isFormData ? data : JSON.stringify(data);
    }

    try {
        const response = await fetch(`${baseUrl}${url}`, options);

        // Handle non-JSON responses (e.g. PDF downloads)
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
            if (response.ok) {
                const blob = await response.blob();
                return { ok: true, status: response.status, data: blob };
            }
            return { ok: false, status: response.status, data: null };
        }

        const responseData = await response.json();
        return { ok: response.ok, status: response.status, data: responseData };
    } catch (error) {
        return { ok: false, status: 0, error: error.message };
    }
};

export default publicApiCall;
